import React, { useCallback, useRef } from 'react';
import { getListBrowserStore } from '@ts-private/list-browser';
import { Card } from '@totalsoft/rocket-ui';
import ListAltIcon from '@mui/icons-material/ListAlt';
import { PAGED_TEXT_RESOURCE_LIST_QUERY } from '../queries';
import AddTextResourceModal from './add/AddTextResourceModal';
import { useMemo } from 'react';
import ApolloFetchingComponent from 'features/common/components/apollo-fetching/ApolloFetchingComponent';
import TableListBrowser from 'features/common/components/table-list-browser/TableListBrowser';
import TextResourceListItem from './TextResourceListItem';
import { useMutation } from 'hooks/apollo/useMutation';
import { ADD_OR_UPDATE_TEXT_RESOURCE, DELETE_TEXT_RESOURCE_MUTATION } from '../mutations';

export const textResourceListStore = getListBrowserStore({
  filterDefinitions: [
    {
      name: 'textResourceCode',
      caption: 'TextResource.Code',
      type: 'text'
    },
    {
      name: 'textResourceLabel',
      caption: 'TextResource.Label',
      type: 'text'
    }
  ],
  orderByFields: [
    {
      caption: 'TextResource.Code',
      value: 'Code'
    },
    {
      caption: 'TextResource.Label',
      value: 'Label'
    }
  ],
  pager: {
    orderBy: 'Code',
    direction: 'asc'
  }
});

const TextResourceListBrowser = () => {
  const itemPendingManagersRef = useRef([]);

  const listFetchingOptions = useMemo(
    () => ({
      queryOptions: {
        query: PAGED_TEXT_RESOURCE_LIST_QUERY,
        name: 'pagedTextResources'
      }
    }),
    []
  );

  // #region Handlers

  const [addOrUpdateTextResource] = useMutation(ADD_OR_UPDATE_TEXT_RESOURCE, {
    onError: ({ params }) => {
      if (params && params.id) {
        const pendingManager = itemPendingManagersRef.current.find(x => x.id === params.id);
        if (pendingManager) pendingManager.handler();
      }
    }
  });

  const handleSave = useCallback(
    input => {
      addOrUpdateTextResource({ variables: { input }, errorHandlingParams: { id: input.id } });
    },
    [addOrUpdateTextResource]
  );

  const [deleteTextResource] = useMutation(DELETE_TEXT_RESOURCE_MUTATION, {
    onError: ({ params }) => {
      if (params && params.id) {
        const pendingManager = itemPendingManagersRef.current.find(x => x.id === params.id);
        if (pendingManager) pendingManager.handler();
      }
    }
  });

  const handleDelete = useCallback(
    id => {
      deleteTextResource({
        variables: {
          input: { id }
        },
        errorHandlingParams: { id }
      });
    },
    [deleteTextResource]
  );

  // #endregion

  const columns = useMemo(
    () => [
      { name: 'code', caption: 'TextResource.Code' },
      { name: 'label', caption: 'TextResource.Label' },
      { name: 'labelENU', caption: 'TextResource.LabelENU' },
      { name: 'labelFRA', caption: 'TextResource.LabelFRA' },
      { name: 'actions', caption: 'Actions' }
    ],
    []
  );

  const itemComponent = useCallback(
    ({ data }) => (
      <TextResourceListItem data={data} onSave={handleSave} onDelete={handleDelete} pendingManagersRef={itemPendingManagersRef} />
    ),
    [handleSave, handleDelete]
  );

  return (
    <Card icon={ListAltIcon}>
      <TableListBrowser name='text-resources-list-browser' store={textResourceListStore} ItemComponent={itemComponent} columns={columns} />
      <AddTextResourceModal store={textResourceListStore} />
      <ApolloFetchingComponent store={textResourceListStore} options={listFetchingOptions} />
    </Card>
  );
};

export default TextResourceListBrowser;
